/**
 * Entity Highlight Component - COGITO Email Intelligence System
 *
 * Renders email content with color-coded entity highlights:
 * - Person (me/user): Purple
 * - Person (other): Blue
 * - Organization: Purple
 * - Date/Time: Orange
 * - Money Amount: Green
 * - Action Item: Red
 *
 * Supports both plain text (position-based) and HTML (text-matching) content.
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

// Entity type colors
const ENTITY_COLORS: Record<string, { bg: string; text: string; hover: string; dot: string; hex: string }> = {
  PERSON: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    hover: 'hover:bg-blue-200',
    dot: 'bg-blue-500',
    hex: '#3b82f6',
  },
  PERSON_ME: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    hover: 'hover:bg-purple-200',
    dot: 'bg-purple-500',
    hex: '#8b5cf6',
  },
  ORGANIZATION: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    hover: 'hover:bg-purple-200',
    dot: 'bg-purple-500',
    hex: '#8b5cf6',
  },
  DATE: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    hover: 'hover:bg-orange-200',
    dot: 'bg-orange-500',
    hex: '#f97316',
  },
  AMOUNT: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    hover: 'hover:bg-green-200',
    dot: 'bg-green-500',
    hex: '#22c55e',
  },
  ACTION: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    hover: 'hover:bg-red-200',
    dot: 'bg-red-500',
    hex: '#ef4444',
  },
};

interface EntityAnnotation {
  start: number;
  end: number;
  text: string;
  entity_type: string;
  entity_id?: string;
  color?: string;
}

interface EntityDetails {
  id?: string;
  name: string;
  type: string;
  role?: string;
  email?: string;
  cogito_id?: string;
  contact_id?: string;
}

interface EntityHighlightProps {
  content: string;
  annotations?: EntityAnnotation[];
  isHtml?: boolean;  // If true, content is HTML and annotations use text-matching
  onEntityClick?: (entity: EntityDetails) => void;
  showTooltips?: boolean;
  className?: string;
}

/**
 * Entity tooltip shown on hover
 */
function EntityTooltip({
  entity,
  position,
  onClose,
}: {
  entity: EntityDetails;
  position: { x: number; y: number };
  onClose: () => void;
}) {
  const { t } = useTranslation('email');
  const colors = ENTITY_COLORS[entity.type] || ENTITY_COLORS.PERSON;

  return (
    <div
      className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-3 max-w-xs"
      style={{
        left: Math.min(position.x, window.innerWidth - 280),
        top: position.y + 10,
      }}
      onMouseLeave={onClose}
    >
      <div className="flex items-start gap-2">
        <div className={`w-2 h-2 rounded-full mt-1.5 ${colors.dot}`} />
        <div className="flex-1">
          <p className="font-medium text-gray-900">{entity.name}</p>
          <p className="text-xs text-gray-500 capitalize mt-0.5">{entity.type.toLowerCase().replace('_', ' ')}</p>
          {entity.role && <p className="text-xs text-gray-600 mt-1">{entity.role}</p>}
          {entity.email && (
            <a href={`mailto:${entity.email}`} className="text-xs text-blue-600 hover:underline mt-1 block">
              {entity.email}
            </a>
          )}
        </div>
      </div>
      {(entity.cogito_id || entity.contact_id) && (
        <div className="mt-2 pt-2 border-t border-gray-100 flex gap-2">
          {entity.contact_id && (
            <button className="text-xs text-blue-600 hover:underline">{t('intelligence.view_contact')}</button>
          )}
          {entity.cogito_id && (
            <button className="text-xs text-purple-600 hover:underline">{t('intelligence.view_in_cogito')}</button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Highlighted entity span
 */
function HighlightedEntity({
  text,
  entityType,
  entityId: _entityId,
  onClick,
  onHover,
}: {
  text: string;
  entityType: string;
  entityId?: string;
  onClick?: () => void;
  onHover?: (e: React.MouseEvent) => void;
}) {
  void _entityId; // Reserved for future entity linking
  const colors = ENTITY_COLORS[entityType] || ENTITY_COLORS.PERSON;

  return (
    <span
      className={`inline-block px-1 py-0.5 rounded-sm cursor-pointer transition-colors ${colors.bg} ${colors.text} ${colors.hover}`}
      onClick={onClick}
      onMouseEnter={onHover}
      role="button"
      tabIndex={0}
      title={`${entityType}: ${text}`}
    >
      {text}
    </span>
  );
}

/**
 * Entity badge for HTML content
 */
function EntityBadge({
  annotation,
  onClick,
}: {
  annotation: EntityAnnotation;
  onClick?: () => void;
}) {
  const colors = ENTITY_COLORS[annotation.entity_type] || ENTITY_COLORS.PERSON;

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${colors.bg} ${colors.text} ${colors.hover}`}
      title={`${annotation.entity_type}: ${annotation.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
      {annotation.text}
    </button>
  );
}

/**
 * Main component that renders text with entity highlights
 */
export default function EntityHighlight({
  content,
  annotations = [],
  isHtml = false,
  onEntityClick,
  showTooltips = true,
  className = '',
}: EntityHighlightProps) {
  const { t } = useTranslation('email');
  const [tooltip, setTooltip] = useState<{
    entity: EntityDetails;
    position: { x: number; y: number };
  } | null>(null);
  const htmlContainerRef = useRef<HTMLDivElement>(null);

  // For HTML content, apply text-based highlighting after render
  useEffect(() => {
    if (!isHtml || !htmlContainerRef.current || annotations.length === 0) return;

    // Get all text nodes in the container
    const walker = document.createTreeWalker(
      htmlContainerRef.current,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    // For each annotation, find and highlight matching text
    annotations.forEach((annotation) => {
      const searchText = annotation.text.toLowerCase();
      const colors = ENTITY_COLORS[annotation.entity_type] || ENTITY_COLORS.PERSON;

      textNodes.forEach((textNode) => {
        const text = textNode.textContent || '';
        const lowerText = text.toLowerCase();
        const index = lowerText.indexOf(searchText);

        if (index !== -1 && textNode.parentNode) {
          // Split the text node and wrap the match in a highlight span
          const before = text.substring(0, index);
          const match = text.substring(index, index + annotation.text.length);
          const after = text.substring(index + annotation.text.length);

          const fragment = document.createDocumentFragment();

          if (before) {
            fragment.appendChild(document.createTextNode(before));
          }

          const highlightSpan = document.createElement('span');
          highlightSpan.textContent = match;
          highlightSpan.style.backgroundColor = colors.hex + '30'; // 30 = ~20% opacity
          highlightSpan.style.borderRadius = '2px';
          highlightSpan.style.padding = '0 2px';
          highlightSpan.title = `${annotation.entity_type}: ${annotation.text}`;
          highlightSpan.className = 'entity-highlight';
          fragment.appendChild(highlightSpan);

          if (after) {
            fragment.appendChild(document.createTextNode(after));
          }

          textNode.parentNode.replaceChild(fragment, textNode);
        }
      });
    });

    // Cleanup function to remove highlights
    return () => {
      if (htmlContainerRef.current) {
        const highlights = htmlContainerRef.current.querySelectorAll('.entity-highlight');
        highlights.forEach((el) => {
          const text = el.textContent || '';
          el.parentNode?.replaceChild(document.createTextNode(text), el);
        });
      }
    };
  }, [isHtml, annotations, content]);

  // For HTML content, render safely and show entity badges
  if (isHtml) {
    const sanitizedHtml = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li',
                     'table', 'tr', 'td', 'th', 'thead', 'tbody', 'span', 'div', 'img',
                     'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'hr'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'width', 'height', 'border',
                     'cellpadding', 'cellspacing', 'bgcolor', 'align', 'valign', 'colspan', 'rowspan'],
    });

    // Group annotations by type
    const groupedAnnotations = annotations.reduce((acc, ann) => {
      const type = ann.entity_type;
      if (!acc[type]) acc[type] = [];
      // Deduplicate by text
      if (!acc[type].some((a) => a.text.toLowerCase() === ann.text.toLowerCase())) {
        acc[type].push(ann);
      }
      return acc;
    }, {} as Record<string, EntityAnnotation[]>);

    return (
      <div className={`relative ${className}`}>
        {/* Render HTML content */}
        <div
          ref={htmlContainerRef}
          className="email-html-content prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />

        {/* Entity badges below content */}
        {annotations.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500 mb-2">{t('intelligence.detected_entities')}</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(groupedAnnotations).map(([type, anns]) => (
                <div key={type} className="flex flex-wrap gap-1">
                  {anns.map((ann, idx) => (
                    <EntityBadge
                      key={`${type}-${idx}`}
                      annotation={ann}
                      onClick={() => {
                        onEntityClick?.({
                          id: ann.entity_id,
                          name: ann.text,
                          type: ann.entity_type,
                        });
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tooltip */}
        {tooltip && showTooltips && (
          <EntityTooltip
            entity={tooltip.entity}
            position={tooltip.position}
            onClose={() => setTooltip(null)}
          />
        )}
      </div>
    );
  }

  // Sort annotations by start position and filter overlaps (for plain text)
  const sortedAnnotations = useMemo(() => {
    const sorted = [...annotations].sort((a, b) => a.start - b.start);

    // Remove overlapping annotations (keep first one)
    const nonOverlapping: EntityAnnotation[] = [];
    let lastEnd = 0;

    for (const ann of sorted) {
      if (ann.start >= lastEnd) {
        nonOverlapping.push(ann);
        lastEnd = ann.end;
      }
    }

    return nonOverlapping;
  }, [annotations]);

  // Build rendered content with highlights (for plain text)
  const renderedContent = useMemo(() => {
    if (sortedAnnotations.length === 0) {
      return <span>{content}</span>;
    }

    const parts: React.ReactNode[] = [];
    let currentIndex = 0;

    sortedAnnotations.forEach((annotation, idx) => {
      // Add text before this annotation
      if (annotation.start > currentIndex) {
        parts.push(
          <span key={`text-${idx}`}>
            {content.slice(currentIndex, annotation.start)}
          </span>
        );
      }

      // Add highlighted entity
      parts.push(
        <HighlightedEntity
          key={`entity-${idx}`}
          text={annotation.text}
          entityType={annotation.entity_type}
          entityId={annotation.entity_id}
          onClick={() => {
            onEntityClick?.({
              id: annotation.entity_id,
              name: annotation.text,
              type: annotation.entity_type,
            });
          }}
          onHover={(e) => {
            if (showTooltips) {
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltip({
                entity: {
                  id: annotation.entity_id,
                  name: annotation.text,
                  type: annotation.entity_type,
                },
                position: { x: rect.left, y: rect.bottom },
              });
            }
          }}
        />
      );

      currentIndex = annotation.end;
    });

    // Add remaining text
    if (currentIndex < content.length) {
      parts.push(<span key="text-end">{content.slice(currentIndex)}</span>);
    }

    return parts;
  }, [content, sortedAnnotations, onEntityClick, showTooltips]);

  return (
    <div className={`relative ${className}`}>
      <div className="whitespace-pre-wrap">{renderedContent}</div>

      {/* Tooltip */}
      {tooltip && showTooltips && (
        <EntityTooltip
          entity={tooltip.entity}
          position={tooltip.position}
          onClose={() => setTooltip(null)}
        />
      )}
    </div>
  );
}

/**
 * Legend component showing what each color means
 */
export function EntityHighlightLegend({ className = '' }: { className?: string }) {
  const { t } = useTranslation('email');
  const items = [
    { type: 'PERSON', labelKey: 'intelligence.person' },
    { type: 'ORGANIZATION', labelKey: 'intelligence.organization' },
    { type: 'DATE', labelKey: 'intelligence.date_time' },
    { type: 'AMOUNT', labelKey: 'intelligence.amount' },
    { type: 'ACTION', labelKey: 'intelligence.action_item' },
  ];

  return (
    <div className={`flex flex-wrap gap-3 text-xs ${className}`}>
      {items.map(({ type, labelKey }) => {
        const colors = ENTITY_COLORS[type];
        return (
          <div key={type} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
            <span className="text-gray-600">{t(labelKey)}</span>
          </div>
        );
      })}
    </div>
  );
}
